import { useState, useEffect } from 'react';
import { X, ChevronRight } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

interface OnboardingStep {
  title: string;
  description: string;
  icon: string;
  content: string;
}

const steps: OnboardingStep[] = [
  {
    title: 'Welcome to Your Business Portal!',
    description: 'Your legal toolkit for building a protected, scalable business',
    icon: '🎉',
    content: 'This is your central hub for all legal documents, business resources, and progress tracking. Everything you need to protect your business is right here.'
  },
  {
    title: 'Your Free Checklist is Ready',
    description: 'Access your SA Business Legal Roadmap',
    icon: '📋',
    content: 'Click "My Downloads" in the sidebar to access your free Essential Contract Clauses Checklist and any other products you own.'
  },
  {
    title: 'Track Your Business Journey',
    description: 'Follow your progress through 4 stages of business growth',
    icon: '🗺️',
    content: 'Visit the "Business Journey" page to see exactly what legal documents you need at each stage of growth, from foundation to scaling.'
  }
];

export default function OnboardingModal() {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    // Check if user has completed onboarding
    if (user) {
      const hasCompletedOnboarding = localStorage.getItem(
        `portal-onboarding-complete-${user.uid}`
      );

      if (!hasCompletedOnboarding) {
        // Small delay to let the portal load first
        setTimeout(() => setIsOpen(true), 500);
      }
    }
  }, [user]);

  const completeOnboarding = () => {
    if (user) {
      localStorage.setItem(`portal-onboarding-complete-${user.uid}`, 'true');
    }
    setIsOpen(false);
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeOnboarding();
    }
  };

  const handleSkip = () => {
    completeOnboarding();
  };

  if (!isOpen) return null;

  const step = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleSkip}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-8 transform transition-all">
          {/* Close button */}
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Icon */}
          <div className="text-6xl mb-4 text-center">{step.icon}</div>

          {/* Content */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {step.title}
            </h2>
            <p className="text-gray-600 mb-4">{step.description}</p>
            <p className="text-gray-700">{step.content}</p>
          </div>

          {/* Progress dots */}
          <div className="flex justify-center gap-2 mb-6">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`h-2 w-2 rounded-full transition-all ${
                  index === currentStep
                    ? 'bg-blue-600 w-6'
                    : index < currentStep
                    ? 'bg-blue-400'
                    : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between">
            <button
              onClick={handleSkip}
              className="text-gray-600 hover:text-gray-900 text-sm font-medium"
            >
              Skip tour
            </button>

            <button
              onClick={handleNext}
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition font-medium"
            >
              {isLastStep ? (
                'Get Started'
              ) : (
                <>
                  Next
                  <ChevronRight className="ml-2 h-4 w-4" />
                </>
              )}
            </button>
          </div>

          {/* Step counter */}
          <div className="text-center mt-4 text-xs text-gray-500">
            Step {currentStep + 1} of {steps.length}
          </div>
        </div>
      </div>
    </div>
  );
}
